/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Constants } from '../../core/constants';
import { ContentType, InteractiveComponents } from '../../core/enums';
import { NdvrInteractor } from '../../core/interactors';
import { ScreenTaskControllerTypes } from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import type { TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import { TVodPlayActionStateType } from '../../core/types';
import { ListTypes } from '../../core/utils';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../interactivelayer/interactiveLayer.reducers';
import { setListEntryPosition } from '../list/list.actions';
import { onKeyDownList, onKeyUpList, onMouseOutList, onMouseOverList } from '../list/list.reducers';
import {
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showPCPinOverlayWithProgramRating,
} from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    onKeyOkVodPlayErrorOverlay,
    onMouseClickVodPlayErrorOverlay,
} from '../overlay/vodplayerroroverlay/vodplayerroroverlay.reducers';
import {
    onKeyOkVodUpSellOverlay,
    onMouseClickVodUpSellOverlay,
} from '../overlay/vodupselloverlay/vodupselloverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../overlaydialog/overlaydialog.reducers';
import { programScreenTransition } from '../screens/navigation/screensnavigation';
import { showPreviousScreen } from '../screens/screens.reducers';
import { notifyVodPlayActionState } from '../vodplayaction/vodplayaction.reducers';
import { showWhisper } from '../whisper/whisper.reducer';
import {
    setListEntryActions,
    setListEntryPageActions,
    setListViewScreenGhostModeAction,
    updateListScreenCaretVisibilityAction,
} from './listviewscreen.actions';
import {
    LIST_SCREEN_GHOST_HIGHLIGHT,
    LIST_SCREEN_LIMIT,
    LIST_SCREEN_UPDATE_CARET_VISIBLE,
    SET_LIST_ENTRY_PAGE,
    SET_VIEW_SCREEN_LIST_ENTRY_POSITION,
} from './listviewscreen.types';

interface IListViewScreen {
    position: number;
    limit: number;
    page: number;
    isCaretVisible: boolean;
    ghostMode: boolean;
}
const initialState: IListViewScreen = {
    position: 0,
    limit: 18, // Default limit for the number of entries displayed in the list view
    page: 0, // Default page number for pagination
    isCaretVisible: false,
    ghostMode: false,
};

const listViewScreenReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IListViewScreen>>{},
) => {
    switch (type) {
        case SET_VIEW_SCREEN_LIST_ENTRY_POSITION:
            return { ...state, position: payload.position };
        case SET_LIST_ENTRY_PAGE:
            return { ...state, page: payload.page };
        case LIST_SCREEN_GHOST_HIGHLIGHT:
            return { ...state, ghostMode: payload.ghostMode };
        case LIST_SCREEN_UPDATE_CARET_VISIBLE:
            return { ...state, isCaretVisible: payload.isCaretVisible };
        case LIST_SCREEN_LIMIT:
            return { ...state, limit: payload.limit };
        default:
            return state;
    }
};
const doAdultParentalControlCheck =
    (isAdult: boolean, callBack: Function, ...callBackParams: any[]) =>
    (dispatch: Function) => {
        const services = new NdvrInteractor();
        if (isAdult && services.shouldHideMetadata()) {
            dispatch(
                showPCPinOverlayWithProgramRating(() => {
                    dispatch(callBack(...callBackParams));
                }, isAdult),
            );
            return;
        }
        dispatch(callBack(...callBackParams, showWhisper));
    };

export const onKeyDownListView = () => (dispatch: Function, getState: Function) => {
    const { listViewScreen } = getState();
    const { position, ghostMode, page, limit } = listViewScreen;
    const listPosition = getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;

    if (ghostMode) {
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                const onDemandSharedPref = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;
                const totalCount = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);

                if (onDemandSharedPref.type === 'onDemand') {
                    const isHeader = onDemandSharedPref.flattenedList[listPosition + 1]?.isHeader;
                    if (isHeader) {
                        const currentPosition =
                            getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
                        dispatch(
                            setListEntryPosition(
                                ListTypes.ON_DEMAND_LIST,
                                currentPosition + 1 || 0,
                            ),
                        );
                    }
                    if (listPosition === limit && totalCount > (page + 1) * limit) {
                        dispatch(setListEntryPageActions(page + 1));
                        dispatch(setListEntryActions(0));
                    }
                    dispatch(onKeyDownList(ListTypes.ON_DEMAND_LIST));
                } else {
                    if (position === limit - 1 && totalCount > (page + 1) * limit) {
                        dispatch(setListEntryPageActions(page + 1));
                    }
                    if (page * limit + position >= totalCount - 1) return;
                    dispatch(setListEntryActions((position + 1) % limit));
                }
            }
            break;
        default:
    }
};

export const onKeyUpListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { listViewScreen } = getState();
    const { position, ghostMode, page, limit } = listViewScreen;
    const listPosition = getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;

    if (ghostMode) {
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                const onDemandSharedPref = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;
                if (onDemandSharedPref.type === 'onDemand') {
                    const isHeader = onDemandSharedPref.flattenedList[listPosition - 1]?.isHeader;
                    if (listPosition === 1 && isHeader) return;
                    if (isHeader) {
                        const currentPosition =
                            getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
                        dispatch(
                            setListEntryPosition(
                                ListTypes.ON_DEMAND_LIST,
                                currentPosition - 1 || 0,
                            ),
                        );
                    }
                    if (page && listPosition === 0) {
                        dispatch(setListEntryPageActions(page - 1));
                        dispatch(setListEntryPosition(ListTypes.ON_DEMAND_LIST, limit));
                        return;
                    }
                    dispatch(onKeyUpList(ListTypes.ON_DEMAND_LIST));
                } else {
                    if (!page && position <= 0) {
                        return; // Prevent going above the first entry
                    }
                    if (page && position === 0) {
                        dispatch(setListEntryPageActions(page - 1));
                    }
                    dispatch(setListEntryActions((position - 1 + limit) % limit));
                }
            }
            break;
        default:
    }
};

export const onKeyOkListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_KEY_OK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onKeyOkVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onKeyOkVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            {
                // Save current position to screen properties for back stickiness
                let listItemInfo = new SharedPreferences(
                    SharedPrefConstants.LIST_SCREEN_PREF_KEY,
                ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

                if (listItemInfo.type === 'onDemand') {
                    const currentPosition =
                        getState().list[ListTypes.ON_DEMAND_LIST]?.position ?? 0;
                    const currScreen = ScreenController.getInstance().getScreen();
                    const currScreenProperties = currScreen.getProperties() || {};
                    currScreen.setProperties({
                        ...currScreenProperties,
                        highlight: currentPosition,
                    });
                    listItemInfo = listItemInfo.flattenedList[currentPosition];
                    dispatch(
                        doAdultParentalControlCheck(
                            listItemInfo?.vodItemKernel?.isAdult,
                            programScreenTransition,
                            {
                                id: listItemInfo.contentId,
                                contentType: ContentType.CONTENT_ID,
                                partnerId: (currScreenProperties as any).partnerId || '',
                                collapsedVodItemId:
                                    (currScreenProperties as any).collapsedVodItemId || '',
                            },
                        ),
                    );
                } else {
                    dispatch(
                        doAdultParentalControlCheck(
                            listItemInfo?.vodItemKernel?.isAdult,
                            programScreenTransition,
                            {
                                id:
                                    listItemInfo.type === 'upcoming'
                                        ? listItemInfo.offerId
                                        : listItemInfo?.vodItemKernel?.offerId,
                                contentType:
                                    listItemInfo.type === 'upcoming'
                                        ? ContentType.OFFER_ID
                                        : ContentType.VOD_OFFER_ID,
                            },
                        ),
                    );
                }
            }
            break;
        default:
    }
};

export const onKeyPlayListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    if (screenTaskController !== ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK) {
        return;
    }
    const listItemInfo = new SharedPreferences(SharedPrefConstants.LIST_SCREEN_PREF_KEY).getJson(
        SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION,
    ) as any;
    if (!listItemInfo) {
        return;
    }
    dispatch(
        notifyVodPlayActionState({
            type: TVodPlayActionStateType.INIT,
            vodRequiredParams: {
                id: listItemInfo?.vodItemKernel?.offerId,
                itemType: 'offer',
                partnerId: listItemInfo?.vodItemKernel?.videoProviderId,
            },
        }),
    );
};

export const onKeyLeftListView = () => (dispatch: Function, getState: Function) => {
    const { ghostMode } = getState().listViewScreen;
    if (ghostMode) {
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;
        default:
    }
};

export const onKeyRightListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { ghostMode } = getState().listViewScreen;

    if (ghostMode) {
        dispatch(setListViewScreenGhostModeAction(false));
        return;
    }

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;
        default:
    }
};

export const onKeyDigitListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        default:
    }
};

export const onKeyBackListView = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            dispatch(showPreviousScreen());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;
        default:
    }
};

export const listScreenSetCaretInvisible = () => (dispatch: Function) => {
    dispatch(clearActiveInteractiveComponent());
    dispatch(updateListScreenCaretVisibilityAction(false));
};

export const listScreenSetCaretVisible = () => (dispatch: Function) => {
    dispatch(updateListScreenCaretVisibilityAction(true));
};

export const onMouseOverListView = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
        listViewScreen: { page, limit },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            dispatch(handleDefaultListScreenMouseOver(value, page, limit));
            break;
        default:
    }
};

const handleDefaultListScreenMouseOver =
    (value: string, page: number, limit: number) => (dispatch: Function) => {
        const totalCount = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);

        if (value === Constants.VIDEO_PLAYER_UI_ID) return;

        if (value === InteractiveComponents.NORTH && page) {
            dispatch(listScreenSetCaretVisible());
            dispatch(setActiveInteractiveComponent(value));
            return;
        }

        if (value === InteractiveComponents.SOUTH && (page + 1) * limit < totalCount) {
            dispatch(listScreenSetCaretVisible());
            dispatch(setActiveInteractiveComponent(value));
            return;
        }

        const onDemandSharedPref = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

        if (onDemandSharedPref.type === 'onDemand') {
            dispatch(onMouseOverList());
        } else {
            // Handle regular list item mouse over
            dispatch(setListViewScreenGhostModeAction(false));
            const position = value.split('_')[0];
            dispatch(setListEntryActions(Number(position) || 0));
        }
    };

export const onMouseOutListView = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            dispatch(handleDefaultListScreenMouseOut(value));
            break;
        default:
    }
};

const handleDefaultListScreenMouseOut = (value: string) => (dispatch: Function) => {
    dispatch(setListViewScreenGhostModeAction(true));

    if (value === InteractiveComponents.NORTH || value === InteractiveComponents.SOUTH) {
        dispatch(clearActiveInteractiveComponent());
        dispatch(listScreenSetCaretInvisible());
    }
    const onDemandSharedPref = new SharedPreferences(
        SharedPrefConstants.LIST_SCREEN_PREF_KEY,
    ).getJson(SharedPrefConstants.LIST_VIEW_SCREEN_SELECTED_OPTION) as any;

    if (onDemandSharedPref.type === 'onDemand') {
        dispatch(onMouseOutList());
    }
};

const handleDefaultListScreenMouseClick =
    (value: string, page: number, limit: number) => (dispatch: Function) => {
        const totalCount = new SharedPreferences(
            SharedPrefConstants.LIST_SCREEN_PREF_KEY,
        ).getNumber(SharedPrefConstants.LIST_SCREEN_TOTAL_COUNT);

        switch (value) {
            case InteractiveComponents.NORTH:
                if (page) {
                    dispatch(setListEntryPageActions(page - 1));
                }
                return;
            case InteractiveComponents.SOUTH:
                if ((page + 1) * limit < totalCount) {
                    dispatch(setListEntryPageActions(page + 1));
                }
                return;
            default:
        }
        dispatch(onKeyOkListView());
    };

export const onMouseClickListView = () => (dispatch: Function, getState: Function) => {
    const {
        inputFactory: { value },
        screens: { screenTaskController },
        listViewScreen: { page, limit },
    } = getState();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_FORMAT_SELECTOR_OVERLAY:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.FORMAT_SELECTOR_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_OVERLAY_TASKS:
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.VOD_RESUME_OVERLAY_MOUSE_CLICK,
                }),
            );
            break;
        case ScreenTaskControllerTypes.VOD_PLAY_ERROR_OVERLAY:
            dispatch(onMouseClickVodPlayErrorOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_UPSELL_OVERLAY_TASKS:
            dispatch(onMouseClickVodUpSellOverlay());
            break;
        case ScreenTaskControllerTypes.DEFAULT_LIST_SCREEN_TASK:
            if (value === Constants.VIDEO_PLAYER_UI_ID) return;
            dispatch(handleDefaultListScreenMouseClick(value, page, limit));
            break;
        default:
    }
};

export const onMouseWheelListView =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
            return; // no action needed
        }

        if (wheelDeltaY < 0) dispatch(onKeyDownListView());
        if (wheelDeltaY > 0) dispatch(onKeyUpListView());
    };

export default listViewScreenReducer;
